import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Components
import Page from 'src/components/Page';
import TabCrm from 'src/sections/@dashboard/home/TabCrm';
import MetricCard from 'src/sections/@dashboard/marketing/analytics/components/MetricCard';

// Shadcn Components
import { Card, CardContent, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/@/components/ui/table';

// Icons
import { Download } from 'lucide-react';

// Utils
import { fCurrency, fPercent } from 'src/utils/formatNumber';
import { fDate, fDateTimeSuffix } from 'src/utils/formatTime';

// Hooks
import useAuth from 'src/hooks/useAuth';

// Actions
import v2_actionCampanhaDetalhes from 'src/actions/v2/marketing/v2_actionCampanhaDetalhes';
import Iconify from 'src/components/Iconify';

import { MKT_METRICAS_DESIGN as d } from './MarketingMetricas.design';

// ----------------------------------------------------------------------

export default function MarketingMetricas() {
  const { cliente } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const campanhaId = searchParams.get('campanha');
  const campanhaNome = searchParams.get('nome');

  // Estados
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [campanhaData, setCampanhaData] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [canalSelecionado, setCanalSelecionado] = useState(cliente._id);
  const [periodo, setPeriodo] = useState('30');
  const [dadosEstados, setDadosEstados] = useState([]);
  const [chatsCapturados, setChatsCapturados] = useState([]);

  // Buscar dados da campanha
  useEffect(() => {
    if (campanhaId) {
      buscarDadosCampanha();
    }
  }, [campanhaId]);

  // Buscar dados dos estados quando apiData estiver disponível
  useEffect(() => {
    if (apiData) {
      buscarDadosEstados();
    }
  }, [apiData]);

  const buscarDadosCampanha = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await v2_actionCampanhaDetalhes(campanhaId);

      if (response.data.error) {
        throw new Error(response.data.message || 'Erro ao buscar dados da campanha');
      }

      const data = response.data.data;
      setApiData(data);

      // Mapear dados da API para a estrutura existente
      const campanhaMapeada = {
        _id: data.campanha.id,
        nome: campanhaNome ? decodeURIComponent(campanhaNome) : data.campanha.nome,
        plataforma: data.campanha.plataforma,
        status: data.campanha.status === 'active' ? 'ativa' : data.campanha.status,
        descricao: data.campanha.descricao,
        orcamento: parseFloat(data.orcamento.valor),
        gasto: parseFloat(data.orcamento.gastoTotal),
        impressoes: data.metricados?.impressoes?.valor || 0,
        cliques: data.metricados?.capturas?.valor || 0,
        conversoes: data.metricados?.conversaoLeads?.valor || 0,
        chats_criados: data.metricados?.chatsCriados?.valor || 0,
        conversao_chats: data.metricados?.conversaoChats?.valor || 0,
        cpc_metricas: data.metricados?.cpc,
        variacao_capturas: data.metricados?.capturas?.variacao,
        variacao_chats_criados: data.metricados?.chatsCriados?.variacao,
        variacao_conversao_chats: data.metricados?.conversaoChats?.variacao,
        publico_alvo: data.informacoes.publicoAlvo,
        regioes: data.informacoes.regioesAtuacao,
        horarios_pico: data.informacoes.horariosPico,
        palavras_chave: data.informacoes.palavrasChave && data.informacoes.palavrasChave.length > 0
          ? data.informacoes.palavrasChave
          : [],
        data_inicio: new Date(data.periodo.dataInicio),
        data_fim: new Date(data.periodo.dataFinal),
        dias_restantes: data.periodo.diasRestantes,
        kpis: data.kpis,
        percentual_utilizado: data.orcamento.percentualUtilizado
      };

      setCampanhaData(campanhaMapeada);

      // Chats capturados
      if (data.chatsCapturados) {
        setChatsCapturados(data.chatsCapturados);
      }

    } catch (error) {
      console.error('Erro ao buscar dados da campanha:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const buscarDadosEstados = async () => {
    try {
      // Usar dados da API se disponíveis
      if (apiData && apiData.distribuicaoGeografica && apiData.distribuicaoGeografica.dddPorEstado) {
        const estadosApi = apiData.distribuicaoGeografica.dddPorEstado;

        // Mapear dados da API para o formato esperado
        const estados = estadosApi.map((estado) => ({
          nome: estado.nome,
          sigla: estado.sigla,
          capturas: estado.capturas || 0,
          cor: estado.cor || `#${Math.floor(Math.random() * 16777215).toString(16)}`
        }));

        // Ordenar por número de capturas (maior para menor)
        estados.sort((a, b) => b.capturas - a.capturas);

        setDadosEstados(estados);
      }
    } catch (error) {
      console.log('Erro ao buscar dados dos estados:', error);
    }
  };

  const calcularDiasRestantes = () => {
    if (!campanhaData) return 0;
    return campanhaData.dias_restantes || 0;
  };

  const exportarChatsExcel = () => {
    if (!chatsCapturados.length) return;

    const headers = ['#', 'Número', 'Nome', 'Funil', 'Estágio', 'Data Criação Chat'];
    const rows = chatsCapturados.map((chat, index) => [
      index + 1,
      chat.chat_numero || chat.phone || '',
      chat.chat_nome || '',
      chat.nome_funil || '',
      chat.estagio_funil || '',
      chat.chat_data_criacao ? fDateTimeSuffix(new Date(chat.chat_data_criacao)) : ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const BOM = '﻿';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `chats-capturados-${campanhaData?.nome || 'campanha'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleVoltar = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Page title="Métricas da Campanha">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-lg text-muted-foreground">Carregando métricas...</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Métricas da Campanha">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <p className="text-lg text-red-600 mb-2">Erro ao carregar dados</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div></div>
      </Page>
    );
  }

  if (!campanhaData) {
    return (
      <Page title="Métricas da Campanha">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <p className="text-lg text-muted-foreground">Campanha não encontrada</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  const cpcFormatado = campanhaData.cpc_metricas?.valor != null
    ? `R$ ${parseFloat(campanhaData.cpc_metricas.valor).toFixed(2)}`
    : (campanhaData.kpis?.cpc || 'N/A');

  const taxaConversaoFormatada = fPercent(campanhaData.conversao_chats * 100);

  const statusBadgeClass = d.statusBadge[campanhaData.status] || d.statusBadge.finalizada;
  const platformConfig = d.platformBadge[campanhaData.plataforma];

  return (
    <Page title={`Métricas da Campanha ${campanhaData.nome}`} className="h-full">
      <div className={d.page.container}>

        {/* Header - Fixed */}
        <div className={d.header.wrapper}>
          <div className={d.header.topRow}>
            <div className={d.header.left}>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleVoltar}
                className={d.header.backBtn}
              >
                <Iconify icon="material-symbols:arrow-back-ios" height={16} width={16} />
              </Button>

              <div className={d.header.iconBox}>
                <Iconify icon="eva:megaphone-outline" width={22} className={d.header.icon} />
              </div>

              <div className="min-w-0">
                <h1 className={d.header.title}>{campanhaData.nome}</h1>
                <p className={d.header.subtitle}>Campanha de marketing</p>
              </div>
            </div>

            <div className={d.header.badgeRow}>
              <span className={statusBadgeClass}>
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                {campanhaData.status}
              </span>
              {platformConfig && (
                <span className={platformConfig.className}>
                  <Iconify icon={platformConfig.icon} width={14} />
                  {platformConfig.label}
                </span>
              )}
            </div>
          </div>

          {/* Metadados */}
          <div className={d.header.metaRow}>
            <div className={d.header.metaItem}>
              <div className={d.header.metaIconBox}>
                <Iconify icon="eva:calendar-outline" width={16} className="text-gray-500" />
              </div>
              <div>
                <p className={d.header.metaLabel}>Período</p>
                <p className={d.header.metaValue}>{fDate(campanhaData.data_inicio)} - {fDate(campanhaData.data_fim)}</p>
              </div>
            </div>

            <div className={d.header.metaItem}>
              <div className={d.header.metaIconBox}>
                <Iconify icon="eva:credit-card-outline" width={16} className="text-gray-500" />
              </div>
              <div>
                <p className={d.header.metaLabel}>Orçamento</p>
                <p className={d.header.metaValue}>R$ {fCurrency(campanhaData.orcamento)}</p>
              </div>
            </div>

            <div className={d.header.metaItem}>
              <div className={d.header.metaIconBox}>
                <Iconify icon="eva:clock-outline" width={16} className="text-gray-500" />
              </div>
              <div>
                <p className={d.header.metaLabel}>Dias restantes</p>
                <p className={d.header.metaValue}>{calcularDiasRestantes()}</p>
              </div>
            </div>
          </div>

          {/* Palavras-chave */}
          {campanhaData.palavras_chave?.length > 0 && (
            <div className={d.header.keywordsWrap}>
              <span className={d.header.keywordsLabel}>
                Palavras-chave ({campanhaData.palavras_chave.length})
              </span>
              {campanhaData.palavras_chave.slice(0, 6).map((key, index) => (
                <span key={index} className={d.header.keywordChip}>
                  {key.value}
                </span>
              ))}
              {campanhaData.palavras_chave.length > 6 && (
                <span className={d.header.keywordChipMore}>
                  +{campanhaData.palavras_chave.length - 6} mais
                </span>
              )}
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className={d.page.scrollArea}>

          {/* Métricas de Capturas e Análise Geográfica */}
          <div className={d.section.wrapper}>
            <div className={d.section.headerRow}>
              <h3 className={d.section.title}>
                <span className={`${d.section.titleIconBox} bg-violet-100`}>
                  <Iconify icon="eva:message-square-outline" width={20} className="text-violet-600" />
                </span>
                Métricas de Conversas
              </h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Métricas de Capturas */}
              <div className={d.stats.grid}>
                <MetricCard
                  icon="eva:cursor-outline"
                  color="blue"
                  value={campanhaData.cliques}
                  label="Chats Capturados"
                  growth={campanhaData.variacao_capturas}
                />

                <MetricCard
                  icon="eva:credit-card-outline"
                  color="violet"
                  value={cpcFormatado}
                  label="CPC (Custo por Captura)"
                />

                <MetricCard
                  icon="eva:message-circle-outline"
                  color="green"
                  value={campanhaData.chats_criados}
                  label="Chats Criados"
                  growth={campanhaData.variacao_chats_criados}
                />

                <MetricCard
                  icon="eva:trending-up-outline"
                  color="amber"
                  value={taxaConversaoFormatada}
                  label="Taxa de Conversão"
                  growth={campanhaData.variacao_conversao_chats}
                />
              </div>

              {/* Tabela de Estados */}
              <div>
                <Card className={d.table.card}>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Iconify icon="eva:pin-outline" width={20} className="text-green-600" />
                      </div>
                      Capturas por Estado
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[60vh] overflow-y-auto">
                      <Table className="relative">
                        <TableHeader>
                          <TableRow className="border-gray-200">
                            <TableHead className={`w-12 ${d.table.headCell}`}>#</TableHead>
                            <TableHead className={d.table.headCell}>Estado</TableHead>
                            <TableHead className={`text-right ${d.table.headCell}`}>Capturas</TableHead>
                            <TableHead className={`text-right ${d.table.headCell}`}>%</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dadosEstados.map((estado, index) => {
                            const totalCapturas = dadosEstados.reduce((sum, e) => sum + e.capturas, 0);
                            const percentual = ((estado.capturas / totalCapturas) * 100).toFixed(1);

                            return (
                              <TableRow key={estado.sigla} className={d.table.row}>
                                <TableCell className="font-medium">
                                  <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm"
                                    style={{ backgroundColor: estado.cor }}
                                  >
                                    {index + 1}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <div className="font-semibold text-gray-900">{estado.nome}</div>
                                    <div className="text-sm text-gray-500">{estado.sigla}</div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right font-bold text-gray-900">
                                  {estado.capturas.toLocaleString('pt-BR')}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-3">
                                    <div className={d.table.progressTrack}>
                                      <div
                                        className={d.table.progressFill}
                                        style={{
                                          width: `${percentual}%`,
                                          backgroundColor: estado.cor
                                        }}
                                      ></div>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-700 w-10">
                                      {percentual}%
                                    </span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Métricas de Leads (CRM) */}
          <div className={d.crm.wrapper}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={d.section.title}>
                <span className={`${d.section.titleIconBox} bg-blue-100`}>
                  <Iconify icon="eva:people-outline" width={20} className="text-blue-600" />
                </span>
                Métricas de Leads (CRM)
              </h3>
            </div>

            {/* Componente TabCrm */}
            <TabCrm
              changeClienteId={setCanalSelecionado}
              changePeriodo={setPeriodo}
              clienteId={canalSelecionado}
              periodo={periodo}
              hidePeriod={true}
              hidePannels={['last_6_months']}
              campaingConfig={
                {
                  start_date: campanhaData.data_inicio,
                  end_date: campanhaData.data_fim,
                  campaign_id: campanhaData._id
                }
              }
            />
          </div>

          {/* Tabela de Chats Capturados */}
          {chatsCapturados.length > 0 && (
            <div className={d.chatsTable.wrapper}>
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h3 className={d.section.title}>
                  <span className={`${d.section.titleIconBox} bg-blue-100`}>
                    <Iconify icon="eva:message-circle-outline" width={20} className="text-blue-600" />
                  </span>
                  Chats Capturados
                  <Badge variant="secondary" className="ml-1">{chatsCapturados.length}</Badge>
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportarChatsExcel}
                  className={d.exportBtn}
                >
                  <Download className="h-4 w-4" />
                  Exportar Excel
                </Button>
              </div>

              <Card className={d.table.card}>
                <CardContent className="p-0">
                  <div className="max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-gray-200">
                          <TableHead className={d.table.headCell}>#</TableHead>
                          <TableHead className={d.table.headCell}>Número</TableHead>
                          <TableHead className={d.table.headCell}>Nome</TableHead>
                          <TableHead className={d.table.headCell}>Funil</TableHead>
                          <TableHead className={d.table.headCell}>Estágio</TableHead>
                          <TableHead className={d.table.headCell}>Data Criação Chat</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {chatsCapturados.map((chat, index) => (
                          <TableRow key={chat._id} className={d.table.row}>
                            <TableCell className="font-medium text-gray-500">{index + 1}</TableCell>
                            <TableCell className="font-medium text-gray-900">
                              {chat.chat_numero || chat.phone || '-'}
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold text-gray-900">
                                {chat.chat_nome || '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              {chat.nome_funil ? (
                                <Badge variant="outline" className="text-xs">
                                  {chat.nome_funil}
                                </Badge>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {chat.estagio_funil ? (
                                <Badge variant="secondary" className="text-xs">
                                  {chat.estagio_funil}
                                </Badge>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-gray-700">
                              {chat.chat_data_criacao ? fDateTimeSuffix(new Date(chat.chat_data_criacao)) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

        </div>
      </div>
    </Page>
  );
}
